import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Pencil, Calculator, FilePlus2, X } from "lucide-react";

const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const h = (n) => `${(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} h`;
const MAT = { base: "Base", trasparente: "Trasparente", pastello: "Pastello", fondo: "Fondo", varie: "Varie" };

const HoursBlock = ({ hours, testid }) => (
  <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid={testid}>
    <div>
      <div className="text-xs uppercase text-muted-foreground mb-1">Ore per operatore</div>
      {hours.operators.length ? hours.operators.map((o) => (
        <div key={o.name} className="flex justify-between border-b border-white/5 py-0.5"><span>{o.name}</span><b>{h(o.hours)}</b></div>
      )) : <div className="text-muted-foreground">Nessuna ora registrata</div>}
      <div className="flex justify-between pt-1 text-[#FF9E00] font-bold"><span>Totale</span><span data-testid={`${testid}-total`}>{h(hours.total)}</span></div>
    </div>
    <div>
      <div className="text-xs uppercase text-muted-foreground mb-1">Materiali usati</div>
      {Object.keys(hours.materials).length ? Object.entries(hours.materials).map(([k, q]) => (
        <div key={k} className="flex justify-between border-b border-white/5 py-0.5"><span>{MAT[k] || k}</span><b>{q}</b></div>
      )) : <div className="text-muted-foreground">Nessun materiale</div>}
    </div>
  </div>
);

const JobResult = ({ job, hours, i }) => (
  <div className="rounded-xl border border-white/10 bg-[#12141A] p-4 space-y-3" data-testid={`plate-result-job-${i}`}>
    <div className="flex flex-wrap items-center gap-2">
      <div className="font-mono-tech text-xs bg-white/5 rounded px-2 py-1">{job.number || "—"}</div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{job.brand} {job.model}{job.status === "closed" && <span className="ml-2 text-[10px] uppercase font-bold bg-[#16A34A]/20 text-[#4ADE80] rounded-full px-2 py-0.5">Fine lavoro</span>}</div>
        <div className="text-xs text-muted-foreground">{job.customer_name || "Cliente non indicato"} · ingresso {fmt(job.date_in)}</div>
      </div>
      <Button asChild size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" data-testid={`plate-result-open-${i}`}><Link to={`/admin/carrozzeria/${job.id}`}><Pencil className="w-4 h-4 mr-1.5" />Apri scheda</Link></Button>
      <Button asChild size="sm" className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid={`plate-result-consuntivo-${i}`}><Link to={`/admin/carrozzeria/${job.id}/consuntivo`}><Calculator className="w-4 h-4 mr-1.5" />Consuntivo</Link></Button>
    </div>
    <HoursBlock hours={hours} testid={`plate-result-hours-${i}`} />
  </div>
);

export const PlateLookup = ({ autoFocus = false }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const plate = q.trim().replace(/\s+/g, " ").toUpperCase();
  useEffect(() => {
    if (plate.replace(/\s/g, "").length < 3) return setRes(null);
    const t = setTimeout(() => api.get(`/admin/bodyshop/by-plate/${encodeURIComponent(plate)}`).then((r) => setRes({ ...r.data, q: plate })).catch(() => setRes(null)), 300);
    return () => clearTimeout(t);
  }, [plate]);

  return (
    <div className="mb-6 rounded-2xl border border-white/10 bg-[#181A22] p-4" data-testid="plate-lookup">
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value.toUpperCase())} placeholder="Cerca targa o cliente"
          className="pl-10 pr-10 h-12 text-base font-mono-tech bg-[#12141A] border-white/10" data-testid="plate-lookup-input" />
        {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label="Cancella" data-testid="plate-lookup-clear"><X className="w-4 h-4" /></button>}
      </div>
      {res && res.q === plate && (
        <div className="mt-4 space-y-3" data-testid="plate-lookup-results">
          {res.jobs.map((r, i) => <JobResult key={r.job.id} job={r.job} hours={r.hours} i={i} />)}
          {!res.jobs.length && (res.all_hours.total > 0 ? (
            <div className="rounded-xl border border-amber-400/30 bg-[#12141A] p-4 space-y-3" data-testid="plate-result-orphan">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-amber-400 mr-auto">Ore registrate su {plate} ma nessuna scheda accettazione</span>
                <Button asChild size="sm" className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="plate-result-create-job">
                  <Link to={`/admin/carrozzeria/nuova?${new URLSearchParams({ targa: res.plate, veicolo: res.all_hours.entries.find((e) => e.vehicle)?.vehicle || "", dal: res.all_hours.entries[0]?.date || "" })}`}><FilePlus2 className="w-4 h-4 mr-1.5" />Crea scheda e collega le ore</Link>
                </Button>
              </div>
              <HoursBlock hours={res.all_hours} testid="plate-result-orphan-hours" />
            </div>
          ) : <p className="text-sm text-muted-foreground" data-testid="plate-lookup-empty">Nessuna scheda né ore per «{q.trim()}».</p>)}
        </div>
      )}
    </div>
  );
};
